/**
 * GP-1405 — one source of truth for how a PICKUP order's workflow stage is
 * WORDED.
 *
 * `workflowStage` says where an order is in fulfilment; it says nothing about
 * HOW the patient receives it. Every stage→label map in the app read the stage
 * alone, so ORD-20260331-140903-284 — `deliveryMethod = 'pickup'`, waiting at
 * the Ottawa counter — announced itself as "Ready to Ship" on the patient's
 * Order & Subscription History while /order-fulfillment/delivery-status listed
 * the same order, the same second, under Pickup / Pending Pickup. The operator
 * is told a bag is going out on a truck when it is sitting on the shelf.
 *
 * The override lives here, alone, because the app has SIX independent
 * stage→label maps (the Patient 360 current-order ribbon, the Order History
 * chip, the All Orders Status/Stage cell, the billing queue's per-drug and
 * row-level in-flight chips, …). Teaching each one about pickups separately is
 * how they drifted apart in the first place.
 *
 * Only the stages whose delivery wording is an outright contradiction are
 * overridden. A pickup order at `packaging` is genuinely in packaging, and
 * renaming that would hide work rather than describe it:
 *
 *   ready_to_ship    → bag is packed and on the shelf          → Ready for Pickup
 *   out_for_delivery → a delivery converted to pickup mid-route → Pending Pickup
 *   delivery_missed     (see `computeTargetStageOnTypeChange` in
 *                       orders.service.ts). The Delivery Status Pickup tab
 *                       already lists both as awaiting collection — these are
 *                       exactly PICKUP_TAB_STAGES minus the two that already
 *                       read correctly (`staging`, `ready_for_pickup`).
 *
 * `delivered` is deliberately NOT remapped: relabelling a real driver drop-off
 * as "Picked Up" would state something that did not happen.
 *
 * No silent fallback — an unknown or missing method returns null and the caller
 * keeps its own wording. Assuming PICKUP here would mislabel every delivery.
 */
import { resolveFulfillmentType } from './fulfillmentType';

/** Stage → what a pickup order at that stage is actually waiting for. */
const PICKUP_STAGE_LABELS: Readonly<Record<string, string>> = {
  ready_to_ship: 'Ready for Pickup',
  out_for_delivery: 'Pending Pickup',
  delivery_missed: 'Pending Pickup',
};

/**
 * Pickup wording for `stage`, or null when the order is not a pickup (or the
 * stage needs no correction). Callers render `pickupStageLabel(...) ?? <their
 * own label>` so a null can never blank a chip.
 */
export function pickupStageLabel(
  stage?: string | null,
  deliveryMethod?: string | null,
): string | null {
  if (resolveFulfillmentType(deliveryMethod) !== 'PICKUP') return null;
  return PICKUP_STAGE_LABELS[String(stage ?? '').trim().toLowerCase()] ?? null;
}

/**
 * Same rule, applied to a whole order/row. Reads the same two fields
 * `deliveryStatusTabFor` does, so the label an order shows and the queue tab it
 * links to can't disagree about what kind of order it is.
 */
export const pickupStageLabelForOrder = (order: any): string | null =>
  pickupStageLabel(
    order?.workflowStage ?? order?.workflow_stage,
    order?.deliveryMethod ?? order?.orderType,
  );
